import {
  ArrowLeft,
  Calendar,
  Calendar1,
  Calendar1Icon,
  CalendarArrowDown,
  CalendarCheck,
  Star as StarIcon,
  ChevronDown,
  X,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

import React from "react";
import { CalendarContainer } from "react-datepicker";
import { CiCalendar, CiCalendarDate } from "react-icons/ci";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useState, useRef, useEffect } from "react";
import { apiClient } from "../../../api";
import { useProperty } from "../../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../../Environments/environment";
import { Link, useSearchParams } from "react-router-dom";
import * as echarts from "echarts";
import { motion, AnimatePresence } from "framer-motion";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

// Add custom styles for the date picker
const customStyles = `
  .react-datepicker {
    font-family: 'Inter', sans-serif;
    border-radius: 0.5rem;
    border: 1px solid #e5e7eb;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  .react-datepicker__header {
    background-color: #fff;
    border-bottom: 1px solid #e5e7eb;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
    padding-top: 0.5rem;
  }
  .react-datepicker__day--selected {
    background-color: #4f46e5 !important;
    color: white !important;
    border-radius: 9999px !important;
  }
  .react-datepicker__day:hover {
    border-radius: 9999px;
  }
  .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker__month-container {
    display: flex;
    flex-direction: column;
  }
  .react-datepicker__month-wrapper {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr) !important;
    gap: 0.5rem !important;
    padding: 0.5rem !important;
    width: 100% !important;
  }
  .react-datepicker__month-text {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 2.5rem !important;
    margin: 0 !important;
    padding: 0.5rem !important;
    border-radius: 0.375rem !important;
    transition: all 0.2s !important;
    text-align: center !important;
  }
  .react-datepicker__month-text:hover {
    background-color: #f3f4f6 !important;
  }
  .react-datepicker__month-text--selected {
    background-color: #4f46e5 !important;
    color: white !important;
  }
  .react-datepicker__month-text--keyboard-selected {
    background-color: #e0e7ff !important;
  }
  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background-color: #e0e7ff !important;
    color: #4338ca !important;
    border-radius: 0 !important;
  }
  .react-datepicker__day--range-start,
  .react-datepicker__day--selecting-range-start {
    background-color: #4f46e5 !important;
    color: #fff !important;
    border-top-left-radius: 9999px !important;
    border-bottom-left-radius: 9999px !important;
  }
  .react-datepicker__day--range-end,
  .react-datepicker__day--selecting-range-end {
    background-color: #4f46e5 !important;
    color: #fff !important;
    border-top-right-radius: 9999px !important;
    border-bottom-right-radius: 9999px !important;
  }
`;

// Shape: { type: 'all' | 'month' | 'range', month: Date|null, start: Date|null, end: Date|null }
const EMPTY_DATE_FILTER = { type: "all", month: null, start: null, end: null };

// Skeleton Loading Component
const ReviewSkeleton = () => (
  <div className="animate-pulse space-y-4">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="bg-white shadow-sm border border-gray-100 rounded-lg p-6"
      >
        <div className="flex flex-col">
          {/* Name Skeleton */}
          <div className="h-6 w-48 bg-gray-200 rounded mb-2"></div>

          {/* Stars Skeleton */}
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <div key={star} className="h-5 w-5 bg-gray-200 rounded"></div>
            ))}
          </div>

          {/* Comment Skeleton */}
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>

          {/* Time Skeleton */}
          <div className="h-4 w-24 bg-gray-200 rounded mt-4 self-end"></div>
        </div>
      </div>
    ))}
  </div>
);

const EfficiencyPieChart = ({ data }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    const chart = echarts.init(chartRef.current);

    const chartData = data.map((item) => ({
      value: item.value,
      count: item.count,
      total: item.total,
      name: item.name.charAt(0).toUpperCase() + item.name.slice(1),
      itemStyle: {
        color:
          item.name === "accepted"
            ? "#22c55e"
            : item.name === "declined"
              ? "#ef4444"
              : "#6b7280",
      },
    }));

    const initialSelection = {};
    data.forEach((item) => {
      initialSelection[item.name] = true;
    });

    const getCenterLabel = () => {
      const acceptedValue =
        data.find((item) => item.name === "accepted")?.value || 0;
      return [
        "{value|" + acceptedValue + "%}",
        "{label|Service",
        "Efficiency}",
      ].join("\n");
    };

    const option = {
      tooltip: {
        trigger: "item",
        formatter: function (params) {
          const count = params.data.count;
          const total = params.data.total;
          return `${params.name}<br/>${params.value}% (${count} of ${total} requests)`;
        },
      },
      legend: {
        orient: "vertical",
        right: 10,
        top: "center",
        selected: initialSelection,
        data: data.map(
          (item) => item.name.charAt(0).toUpperCase() + item.name.slice(1),
        ),
        textStyle: {
          fontSize: 14,
        },
      },
      series: [
        {
          name: "Service Efficiency",
          type: "pie",
          radius: ["40%", "60%"],
          center: ["45%", "50%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: "#fff",
            borderWidth: 2,
          },
          label: {
            show: true,
            position: "center",
            formatter: getCenterLabel(),
            rich: {
              value: {
                fontSize: 36,
                fontWeight: "bold",
                color: "#22c55e",
                padding: [0, 0, 16, 0],
                fontFamily: "'Inter', sans-serif",
              },
              label: {
                fontSize: 16,
                color: "#6b7280",
                lineHeight: 20,
                fontFamily: "'Inter', sans-serif",
              },
            },
          },
          labelLine: {
            show: false,
          },
          data: chartData,
        },
      ],
    };

    chart.setOption(option);

    chart.on("legendselectchanged", () => {
      chart.setOption({
        series: [
          {
            label: {
              formatter: getCenterLabel(),
            },
          },
        ],
      });
    });

    window.addEventListener("resize", chart.resize);

    return () => {
      chart.dispose();
      window.removeEventListener("resize", chart.resize);
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <div
        ref={chartRef}
        className="w-full h-[500px] bg-white rounded-lg shadow-sm border border-gray-100 p-8"
      />
      <div className="max-h-80 overflow-y-auto space-y-4 p-6 bg-white rounded-lg shadow-sm border border-gray-100">
        {data.map((item, idx) => (
          <div key={idx} className="border-b pb-3">
            <div className="flex items-center gap-3 mb-2">
              <span
                className="inline-block w-4 h-4 rounded-full"
                style={{
                  backgroundColor:
                    item.name === "accepted"
                      ? "#22c55e"
                      : item.name === "declined"
                        ? "#ef4444"
                        : "#6b7280",
                }}
              />
              <span className="font-medium text-base">
                {item.name.charAt(0).toUpperCase() + item.name.slice(1)}{" "}
                Requests
              </span>
              <span className="text-sm bg-gray-100 px-3 py-1 rounded-full ml-auto">
                {item.value}%
              </span>
            </div>
            <p className="text-gray-600 text-sm pl-7">
              {item.name === "accepted"
                ? "Successfully completed requests"
                : item.name === "declined"
                  ? "Requests that were declined"
                  : "Requests pending action"}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const DateFilterPicker = ({ filter, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(
    filter.type === "range" ? "range" : "month"
  );
  const [tempStart, setTempStart] = useState(filter.start);
  const [tempEnd, setTempEnd] = useState(filter.end);
  const datePickerRef = useRef(null);

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);

    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, [isOpen]);

  // Sync temp range state when filter changes externally or when popover opens
  useEffect(() => {
    if (isOpen) {
      setTempStart(filter.start);
      setTempEnd(filter.end);
      setActiveTab(filter.type === "range" ? "range" : "month");
    }
  }, [isOpen, filter.start, filter.end, filter.type]);

  const getLabel = () => {
    if (filter.type === "month" && filter.month) {
      return dayjs(filter.month).format("MMMM YYYY");
    }
    if (filter.type === "range" && filter.start && filter.end) {
      const start = dayjs(filter.start);
      const end = dayjs(filter.end);
      const sameYear = start.isSame(end, "year");
      return `${start.format(sameYear ? "MMM DD" : "MMM DD, YYYY")} - ${end.format("MMM DD, YYYY")}`;
    }
    return "All time";
  };

  const hasFilter = filter.type !== "all";

  const tabClass = (tab) =>
    `flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
      activeTab === tab
        ? "bg-indigo-50 text-indigo-700"
        : "text-gray-600 hover:bg-gray-50"
    }`;

  return (
    <div className="relative" ref={datePickerRef}>
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="inline-block"
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-indigo-300 transition-colors duration-200"
        >
          <Calendar className="w-5 h-5 text-indigo-600" />
          <span className="text-gray-700">{getLabel()}</span>
          {hasFilter ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(EMPTY_DATE_FILTER);
              }}
              className="p-0.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Reset to all time"
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          )}
        </button>
      </motion.div>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute z-50 mt-2 right-0"
        >
          <div className="bg-white shadow-lg border border-gray-200 rounded-lg p-3 w-[20rem]">
            <div className="flex gap-1 mb-2 bg-gray-50 p-1 rounded-lg">
              <button
                type="button"
                onClick={() => setActiveTab("month")}
                className={tabClass("month")}
              >
                Month
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("range")}
                className={tabClass("range")}
              >
                Date Range
              </button>
            </div>

            <button
              type="button"
              onClick={() => {
                onChange(EMPTY_DATE_FILTER);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md font-medium mb-1"
            >
              All time
            </button>

            {activeTab === "month" ? (
              <DatePicker
                selected={
                  filter.type === "month" && filter.month
                    ? filter.month
                    : new Date()
                }
                onChange={(date) => {
                  onChange({
                    type: "month",
                    month: date,
                    start: null,
                    end: null,
                  });
                  setIsOpen(false);
                }}
                dateFormat="MM/yyyy"
                showMonthYearPicker
                showFullMonthYearPicker
                inline
                calendarClassName="bg-white"
              />
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <DatePicker
                    selectsRange
                    startDate={tempStart}
                    endDate={tempEnd}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setTempStart(start);
                      setTempEnd(end);
                    }}
                    inline
                    monthsShown={1}
                    calendarClassName="bg-white"
                  />
                </div>
                <div className="text-xs text-gray-600 px-1 flex justify-between">
                  <span>
                    From:{" "}
                    <span className="font-medium text-gray-800">
                      {tempStart ? dayjs(tempStart).format("MMM DD, YYYY") : "—"}
                    </span>
                  </span>
                  <span>
                    To:{" "}
                    <span className="font-medium text-gray-800">
                      {tempEnd ? dayjs(tempEnd).format("MMM DD, YYYY") : "—"}
                    </span>
                  </span>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setTempStart(null);
                      setTempEnd(null);
                    }}
                    className="flex-1 px-3 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    disabled={!tempStart || !tempEnd}
                    onClick={() => {
                      onChange({
                        type: "range",
                        month: null,
                        start: tempStart,
                        end: tempEnd,
                      });
                      setIsOpen(false);
                    }}
                    className="flex-1 px-3 py-2 text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed rounded-md font-medium transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Helper: apply date filter (month or range) when active
const filterByDate = (items, filter, dateKey = "createdAt") => {
  if (!items?.length || !filter || filter.type === "all") return items;
  if (filter.type === "month" && filter.month) {
    return items.filter(
      (item) =>
        dayjs(item[dateKey]).isSame(filter.month, "month") &&
        dayjs(item[dateKey]).isSame(filter.month, "year")
    );
  }
  if (filter.type === "range" && filter.start && filter.end) {
    const start = dayjs(filter.start).startOf("day");
    const end = dayjs(filter.end).endOf("day");
    return items.filter((item) => {
      const d = dayjs(item[dateKey]);
      return d.isSameOrAfter(start) && d.isSameOrBefore(end);
    });
  }
  return items;
};

const Details = () => {
  const [details, setDetails] = useState([]);
  const [dateFilter, setDateFilter] = useState(EMPTY_DATE_FILTER);
  const [loading, setLoading] = useState(false);
  const { selectedPropertyId } = useProperty();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");

  useEffect(() => {
    const filterGuestsByStayOverlap = (guests) => {
      if (dateFilter.type === "all") return guests;
      let rangeStart;
      let rangeEnd;
      if (dateFilter.type === "month" && dateFilter.month) {
        rangeStart = dayjs(dateFilter.month).startOf("month");
        rangeEnd = dayjs(dateFilter.month).endOf("month");
      } else if (
        dateFilter.type === "range" &&
        dateFilter.start &&
        dateFilter.end
      ) {
        rangeStart = dayjs(dateFilter.start).startOf("day");
        rangeEnd = dayjs(dateFilter.end).endOf("day");
      } else {
        return guests;
      }
      return guests.filter((guest) => {
        if (!guest.checkIn || !guest.checkOut) return false;
        const checkIn = dayjs(guest.checkIn);
        const checkOut = dayjs(guest.checkOut);
        return (
          checkIn.isSameOrBefore(rangeEnd, "day") &&
          checkOut.isSameOrAfter(rangeStart, "day")
        );
      });
    };

    const fetchDetails = async () => {
      setLoading(true);
      try {
        if (type === "ratings") {
          const reviewsResponse = await apiClient.get(
            `${baseUrl}/review/${selectedPropertyId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
              },
            },
          );
          const sortedReviews = [
            ...(reviewsResponse.data.result.reviews || []),
          ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const filteredReviews = filterByDate(
            sortedReviews,
            dateFilter,
            "createdAt"
          );
          setDetails(filteredReviews);
        } else if (type === "qrCode") {
          const response = await apiClient.get(
            `${baseUrl}/qrCodeScan/${selectedPropertyId}`,
          );
          const allScans = (response.data.result || []).sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          const filteredScans = filterByDate(
            allScans,
            dateFilter,
            "createdAt"
          );
          setDetails(filteredScans);
        } else if (type === "houseKeeping") {
          const response = await apiClient.get(
            `${baseUrl}/guest/addons/${selectedPropertyId}?requestStatus=all`,
          );
          const allRequests = response.data.result.requests || [];
          const housekeepingRequests = allRequests
            .filter(
              (request) =>
                request.createdAt &&
                (request.requestType === "houseKeeping" ||
                  request.name === "Request for Full housekeeping")
            )
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const filtered = filterByDate(
            housekeepingRequests,
            dateFilter,
            "createdAt"
          );
          setDetails(filtered);
        } else if (type === "service") {
          const response = await apiClient.get(
            `${baseUrl}/guest/addons/${selectedPropertyId}?requestStatus=all`,
          );
          const allRequests = (response.data.result.requests || [])
            .filter((request) => request.createdAt)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          const filtered = filterByDate(
            allRequests,
            dateFilter,
            "createdAt"
          );
          setDetails(filtered);
        } else if (type === "efficiency") {
          const response = await apiClient.get(
            `${baseUrl}/guest/addons/${selectedPropertyId}?requestStatus=all`,
          );
          const allRequests = response.data.result.requests || [];
          const filteredRequests = filterByDate(
            allRequests,
            dateFilter,
            "createdAt"
          );

          const totalRequests = filteredRequests.length;
          const acceptedRequests = filteredRequests.filter(
            (request) => request.requestStatus?.toLowerCase() === "accepted",
          ).length;
          const declinedRequests = filteredRequests.filter(
            (request) => request.requestStatus?.toLowerCase() === "declined",
          ).length;
          const pendingRequests =
            totalRequests - acceptedRequests - declinedRequests;

          const chartData = [
            {
              name: "accepted",
              value:
                totalRequests > 0
                  ? Math.round((acceptedRequests / totalRequests) * 100)
                  : 0,
              count: acceptedRequests,
              total: totalRequests,
            },
            {
              name: "declined",
              value:
                totalRequests > 0
                  ? Math.round((declinedRequests / totalRequests) * 100)
                  : 0,
              count: declinedRequests,
              total: totalRequests,
            },
            {
              name: "pending",
              value:
                totalRequests > 0
                  ? Math.round((pendingRequests / totalRequests) * 100)
                  : 0,
              count: pendingRequests,
              total: totalRequests,
            },
          ].filter((item) => item.count > 0);

          setDetails(chartData);
        } else {
          const params = { currentStatus: "Reservation" };
          const response = await apiClient.get(
            `${baseUrl}/guest/${selectedPropertyId}`,
            { params: params },
          );
          const guests = response.data.result.guests || [];
          setDetails(filterGuestsByStayOverlap(guests.data));
        }
      } catch (error) {
        console.error(error);
        setDetails([]);
      } finally {
        setLoading(false);
      }
    };

    if (type) {
      fetchDetails();
    }
  }, [type, selectedPropertyId, dateFilter]);

  // Format date to readable string with relative time
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
    } else {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      });
    }
  };

  // Render stars based on rating
  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={`w-5 h-5 transform transition-all duration-300 hover:scale-125 ${
          index < rating
            ? "text-yellow-400 fill-yellow-400 animate-starPop"
            : "text-gray-300 hover:text-yellow-200"
        }`}
        style={{
          animationDelay: `${index * 100}ms`,
        }}
      />
    ));
  };

  useEffect(() => {
    // Add the keyframes for star animation to the document
    const style = document.createElement("style");
    style.textContent = `
      @keyframes starPop {
        0% {
          transform: scale(0.5);
          opacity: 0;
        }
        50% {
          transform: scale(1.2);
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }

      .animate-starPop {
        animation: starPop 0.5s ease-out forwards;
      }

      @keyframes spin-slow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }

      .animate-spin-slow {
        animation: spin-slow 3s linear infinite;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Add this right after the ReviewSkeleton component
  const QRCodeSkeleton = () => (
    <div className="animate-pulse space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white shadow-sm border border-gray-100 rounded-lg p-6"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-5 w-24 bg-gray-200 rounded"></div>
                <div className="h-4 w-32 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="h-10 w-16 bg-gray-200 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Add this after QRCodeSkeleton component
  const InHouseGuestSkeleton = () => (
    <div className="animate-pulse space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white shadow-sm border border-gray-100 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-5 w-40 bg-gray-200 rounded"></div>
                <div className="h-4 w-48 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="h-4 w-32 bg-gray-200 rounded"></div>
              <div className="h-8 w-16 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Add this before the return statement
  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  // Add this function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "accepted":
        return "bg-green-100 text-green-800";
      case "declined":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getGuestStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "in house":
        return "bg-green-100 text-green-800";
      case "checked out":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-50 text-yellow-700";
    }
  };

  const ServiceRequestSkeleton = () => (
    <div className="animate-pulse space-y-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white shadow-sm border border-gray-100 rounded-lg p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
              <div className="space-y-2">
                <div className="h-5 w-32 bg-gray-200 rounded"></div>
                <div className="h-6 w-24 bg-gray-200 rounded"></div>
              </div>
            </div>
            <div className="flex space-x-4">
              <div className="h-8 w-20 bg-gray-200 rounded-lg"></div>
              <div className="h-8 w-32 bg-gray-200 rounded-lg"></div>
                </div>
            <div className="space-y-2">
              <div className="h-8 w-24 bg-gray-200 rounded-lg"></div>
              <div className="h-4 w-32 bg-gray-200 rounded self-end"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="sm:flex sm:items-center mt-6 justify-between">
          <Link
            to="/dashboard/analytics"
            className="flex items-center text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-base">Back to Analytics</span>
          </Link>
          {(type === "qrCode" ||
            type === "houseKeeping" ||
            type === "service" ||
            type === "currentInHouseGuests" ||
            type === "ratings" ||
            type === "efficiency") && (
            <DateFilterPicker
              filter={dateFilter}
              onChange={setDateFilter}
            />
          )}
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl text-gray-800 tracking-tight">
              {type === "ratings"
                ? "Guest Reviews"
                : type === "qrCode"
                  ? "QR Code Scans"
                  : type === "currentInHouseGuests"
                    ? "Current In-House Guests"
                    : type === "houseKeeping"
                      ? "Housekeeping Requests"
                      : type === "service"
                        ? "Service Requests"
                        : type === "efficiency"
                          ? "Service Efficiency"
                          : "Analytics Details"}
            </h1>
          </div>

          {loading ? (
            type === "ratings" ? (
              <ReviewSkeleton />
            ) : type === "currentInHouseGuests" ? (
              <InHouseGuestSkeleton />
            ) : type === "houseKeeping" ? (
              <div className="animate-pulse space-y-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white shadow-sm border border-gray-100 rounded-lg p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 bg-gray-200 rounded-full"></div>
                        <div className="space-y-2">
                          <div className="h-5 w-32 bg-gray-200 rounded"></div>
                          <div className="h-6 w-24 bg-gray-200 rounded"></div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-8 w-20 bg-gray-200 rounded-lg"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-8 w-24 bg-gray-200 rounded-lg"></div>
                        <div className="h-4 w-32 bg-gray-200 rounded self-end"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : type === "service" ? (
              <ServiceRequestSkeleton />
            ) : type === "efficiency" ? (
              <div className="animate-pulse space-y-6">
                {/* Pie Chart Skeleton */}
                <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-8 h-[500px] relative">
                  <div className="flex items-center justify-center h-full">
                    {/* Circular Skeleton */}
                    <div className="relative w-[300px] h-[300px]">
                      {/* Outer Ring Animation */}
                      <div className="absolute inset-0 rounded-full border-[30px] border-gray-200 animate-spin-slow"></div>

                      {/* Center Content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="h-12 w-24 bg-gray-200 rounded-full mb-2"></div>
                        <div className="h-4 w-16 bg-gray-200 rounded-full"></div>
                      </div>
                    </div>

                    {/* Legend Skeleton */}
                    <div className="absolute right-8 top-1/2 -translate-y-1/2 space-y-4">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center space-x-3">
                          <div className="w-4 h-4 rounded-full bg-gray-200"></div>
                          <div className="h-4 w-20 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Stats Cards Skeleton */}
                <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
                  <div className="grid grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-4 h-4 rounded-full bg-gray-200"></div>
                          <div className="h-5 w-24 bg-gray-200 rounded"></div>
                          <div className="h-6 w-12 bg-gray-200 rounded-full ml-auto"></div>
                        </div>
                        <div className="h-4 w-full bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
            </div>
            ) : (
              <QRCodeSkeleton />
            )
          ) : details.length === 0 ? (
            <div className="flex justify-center items-center h-64 bg-white rounded-lg shadow-sm border border-gray-100">
              <p className="text-gray-500">
                {(() => {
                  let rangeLabel = null;
                  if (dateFilter.type === "month" && dateFilter.month) {
                    rangeLabel = dayjs(dateFilter.month).format("MMMM YYYY");
                  } else if (
                    dateFilter.type === "range" &&
                    dateFilter.start &&
                    dateFilter.end
                  ) {
                    rangeLabel = `${dayjs(dateFilter.start).format("MMM DD, YYYY")} - ${dayjs(dateFilter.end).format("MMM DD, YYYY")}`;
                  }
                  if (!rangeLabel) return "No data available";
                  switch (type) {
                    case "qrCode":
                      return `No scans in ${rangeLabel}`;
                    case "houseKeeping":
                      return `No housekeeping requests in ${rangeLabel}`;
                    case "service":
                      return `No add-ons requested in ${rangeLabel}`;
                    case "currentInHouseGuests":
                      return `No guests present in ${rangeLabel}`;
                    case "ratings":
                      return `No reviews made in ${rangeLabel}`;
                    default:
                      return "No data available";
                  }
                })()}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {type === "ratings" ? (
                // Reviews layout
                details.map((review, index) => (
                  <div
                    key={index}
                    className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 transition-all hover:shadow-md transform hover:-translate-y-1 duration-200 animate-fadeIn"
                    style={{
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    <div className="flex flex-col">
                      {/* Guest Name */}
                      <div className="text-lg font-medium text-gray-900 mb-2">
                        {review.guestId?.firstName} {review.guestId?.lastName}
                      </div>

                      {/* Star Rating */}
                      <div className="flex items-center gap-1 mb-3">
                        {renderStars(review.overAllRating)}
                      </div>

                      {/* Review Comment */}
                      <p className="text-gray-700 leading-relaxed mb-4">
                        {review.comments || "No comment provided"}
                      </p>

                      {/* Timestamp */}
                      <div className="text-sm text-gray-500 self-end">
                        {formatDate(review.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              ) : type === "qrCode" ? (
                // QR Code scans layout
                details.map((scan, index) => (
                  <div
                    key={index}
                    className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 transition-all hover:shadow-md transform hover:-translate-y-1 duration-200 animate-fadeIn"
                    style={{
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-4">
                        <div className="bg-gradient-to-br from-indigo-600 to-indigo-400 text-white h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold">
                          {scan.roomNumber}
                        </div>
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            Room {scan.roomNumber}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {formatDate(scan.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end">
                        <div className="text-2xl font-bold text-indigo-600">
                          {scan.total}
                        </div>
                        <p className="text-sm text-gray-500">scaned</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : type === "currentInHouseGuests" ? (
                // In-house guests layout
                details.map(
                  (guest, index) => (
                    console.log(guest),
                    (
                      <div
                        key={index}
                        className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 transition-all hover:shadow-lg transform hover:-translate-y-1 duration-200 animate-fadeIn"
                        style={{
                          animationDelay: `${index * 100}ms`,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          {/* Guest Info - Left Section */}
                          <div className="flex items-center space-x-4 w-1/3">
                            <div className="relative group">
                              <div className="bg-gradient-to-br from-indigo-600 to-indigo-400 text-white h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold transform transition-transform duration-300 group-hover:scale-110">
                                {getInitials(guest.firstName, guest.lastName)}
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-full blur opacity-30 group-hover:opacity-40 transition-opacity duration-300"></div>
                            </div>
                            <div className="space-y-1 min-w-0">
                              <h3 className="text-lg font-medium text-gray-900 flex items-center truncate">
                                {guest.firstName} {guest.lastName}
                              </h3>
                              <p className="text-sm text-gray-500 hover:text-indigo-600 transition-colors duration-200 truncate">
                                {guest.email}
                              </p>
                            </div>
                          </div>

                          {/* Dates - Middle Section */}
                          <div className="flex flex-col items-start space-y-2 w-1/3">
                            <div className="flex items-center text-sm text-gray-600 w-full">
                              <Calendar className="w-4 h-4 mr-2 text-indigo-600 flex-shrink-0" />
                              <span className="whitespace-nowrap">
                                Check-in:{" "}
                                {dayjs(guest.checkIn).format("MMM DD, YYYY")}
                              </span>
                            </div>
                            <div className="flex items-center text-sm text-gray-600 w-full">
                              <CalendarCheck className="w-4 h-4 mr-2 text-indigo-600 flex-shrink-0" />
                              <span className="whitespace-nowrap">
                                Check-out:{" "}
                                {dayjs(guest.checkOut).format("MMM DD, YYYY")}
                              </span>
                            </div>
                          </div>

                          {/* Contact & Room - Right Section */}
                          <div className="flex items-center space-x-6 w-1/3 justify-end">
                            <div className="text-sm text-gray-600 whitespace-nowrap">
                              {guest.countryCode} {guest.phoneNumber}
                            </div>
                            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap">
                              Room {guest.roomNumber}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  ),
                )
              ) : type === "houseKeeping" ? (
                details.map(
                  (request, index) => (
                    console.log(request),
                    (
                      <div
                        key={index}
                        className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 transition-all hover:shadow-lg transform hover:-translate-y-1 duration-200 animate-fadeIn"
                        style={{
                          animationDelay: `${index * 100}ms`,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          {/* Guest Info - Left Section */}
                          <div className="flex items-center space-x-4">
                            <div className="relative group">
                              <div className="bg-gradient-to-br from-indigo-600 to-indigo-400 text-white h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold transform transition-transform duration-300 group-hover:scale-110">
                                {getInitials(
                                  request.guestId?.firstName,
                                  request.guestId?.lastName,
                                )}
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-full blur opacity-30 group-hover:opacity-40 transition-opacity duration-300"></div>
                            </div>
                            <div className="space-y-2">
                              <h3 className="text-lg font-medium text-gray-900">
                                {request.guestId?.firstName}{" "}
                                {request.guestId?.lastName}
                              </h3>
                              <div
                                className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getGuestStatusColor(
                                  request.guestId?.status?.currentStatus,
                                )}`}
                              >
                                {request.guestId?.status?.currentStatus ||
                                  "Booking"}
                              </div>
                            </div>
                          </div>

                          {/* Room Number - Middle Section */}
                          <div className="flex items-center">
                            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-medium">
                              Room {request.guestId?.roomNumber}
                            </div>
                          </div>

                          {/* Status and Date - Right Section */}
                          <div className="flex flex-col items-end space-y-2">
                            <div
                              className={`px-4 py-2 rounded-lg font-medium text-sm ${getStatusColor(
                                request.requestStatus,
                              )}`}
                            >
                              {request.requestStatus || "Requested"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(request.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  ),
                )
              ) : type === "service" ? (
                details.map(
                  (request, index) => (
                    console.log(request),
                    (
                      <div
                        key={index}
                        className="bg-white shadow-sm border border-gray-100 rounded-lg p-6 transition-all hover:shadow-lg transform hover:-translate-y-1 duration-200 animate-fadeIn"
                        style={{
                          animationDelay: `${index * 100}ms`,
                        }}
                      >
                        <div className="flex items-center justify-between">
                          {/* Guest Info - Left Section */}
                          <div className="flex items-center space-x-4">
                            <div className="relative group">
                              <div className="bg-gradient-to-br from-indigo-600 to-indigo-400 text-white h-12 w-12 rounded-full flex items-center justify-center text-lg font-semibold transform transition-transform duration-300 group-hover:scale-110">
                                {getInitials(
                                  request.guestId?.firstName,
                                  request.guestId?.lastName,
                                )}
                              </div>
                              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-indigo-400 rounded-full blur opacity-30 group-hover:opacity-40 transition-opacity duration-300"></div>
                            </div>
                            <div className="space-y-2">
                              <h3 className="text-lg font-medium text-gray-900">
                                {request.guestId?.firstName}{" "}
                                {request.guestId?.lastName}
                              </h3>
                              <div
                                className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getGuestStatusColor(
                                  request.guestId?.status?.currentStatus,
                                )}`}
                              >
                                {request.guestId?.status?.currentStatus ||
                                  "Booking"}
                              </div>
                            </div>
                          </div>

                          {/* Room and Request Name - Middle Section */}
                          <div className="flex items-center space-x-4">
                            <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg font-medium">
                              Room {request.guestId?.roomNumber}
                            </div>
                            <div className="bg-purple-50 text-purple-700 px-4 py-2 rounded-lg font-medium">
                              {request.name
                                ? request.name
                                : request.requestType === "lateCheckOut"
                                  ? "Late CheckOut"
                                  : request.requestType === "earlyCheckIn"
                                    ? "Early CheckIn"
                                    : "Request"}
                            </div>
                          </div>

                          {/* Status and Date - Right Section */}
                          <div className="flex flex-col items-end space-y-2">
                            <div
                              className={`px-4 py-2 rounded-lg font-medium text-sm ${getStatusColor(
                                request.requestStatus,
                              )}`}
                            >
                              {request.requestStatus || "Requested"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(request.createdAt)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  ),
                )
              ) : type === "efficiency" ? (
                <EfficiencyPieChart data={details} />
              ) : (
                details.map((detail, index) => (
                  <div
                    key={index}
                    className="flex justify-between p-6 items-center bg-white shadow-sm border border-gray-100 rounded-lg hover:shadow-md transition-all transform hover:-translate-y-1 duration-200 animate-fadeIn"
                    style={{
                      animationDelay: `${index * 100}ms`,
                    }}
                  >
                <div className="flex flex-col">
                      <h3 className="text-lg text-gray-800">
                    {type === "currentInHouseGuests"
                      ? `${detail.firstName} ${detail.lastName}`
                      : `Room ${detail.roomNumber}`}
                  </h3>
                </div>
                <div className="flex flex-col items-end">
                  <p
                        className={`${
                          type === "currentInHouseGuests"
                            ? "text-lg"
                            : "text-3xl"
                        } text-gray-900`}
                  >
                    {type === "currentInHouseGuests"
                      ? `Room ${detail.roomNumber}`
                      : detail.total}
                  </p>
                      <p className="text-sm text-gray-500 mt-1">
                    {type !== "currentInHouseGuests" &&
                      (type === "qrCode" ? "Visits" : "Requested")}
                  </p>
                </div>
              </div>
            ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Details;
